import {
    useQuery,
    useMutation,
    useQueryClient,
    type UseQueryOptions,
    type UseQueryResult,
} from '@tanstack/react-query'
import {
    getUserPreference,
    getAllUserPreferences,
    deleteUserPreference,
    updateUserPreference,
    getRawUserPreference,
    updateRawUserPreference,
} from '../../api/basal-ganglia/user-preferences-management/user-preferences-management'
import {fetchCurrentUser} from '../../api/basal-ganglia/users/users'
import {userPreferencesManagementKeys} from './keys'
import {type UserPreference} from '../../api/basal-ganglia/user-preferences-management/types/user-preferences-management.types'
import {type User} from '../../api/basal-ganglia/users/types/users.types'

export function useQueryUserPreference<T = unknown>(
    key: string,
    options?: UseQueryOptions<UserPreference<T> | null, Error>,
): UseQueryResult<UserPreference<T> | null, Error> {
    return useQuery<UserPreference<T> | null, Error>({
        retry: false,
        queryKey: userPreferencesManagementKeys.preference(key),
        queryFn: () => getUserPreference<T>(key),
        staleTime: 5 * 60 * 1000,
        ...options,
    })
}

export function useQueryAllUserPreferences<T = unknown>(
    options?: UseQueryOptions<UserPreference<T>[], Error>,
): UseQueryResult<UserPreference<T>[], Error> {
    return useQuery<UserPreference<T>[], Error>({
        retry: false,
        queryKey: userPreferencesManagementKeys.user(),
        queryFn: () => getAllUserPreferences<T>(),
        staleTime: 5 * 60 * 1000,
        ...options,
    })
}

export function useUpsertUserPreferenceMutation<T = unknown>() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({key, value}: {key: string; value: T}) => updateUserPreference(key, {value}),
        onSuccess: (data, variables) => {
            const {key} = variables

            queryClient.setQueryData(userPreferencesManagementKeys.preference(key), data)

            queryClient
                .invalidateQueries({
                    queryKey: userPreferencesManagementKeys.user(),
                })
                .catch((error: unknown) => {
                    console.error(
                        `Error invalidating preferences: ${typeof error === 'string' ? error : String(error)}`,
                    )
                })
        },
    })
}

export function useDeleteUserPreferenceMutation() {
    const queryClient = useQueryClient()

    return useMutation({
        mutationFn: ({key}: {key: string}) => deleteUserPreference(key),
        onSuccess: (_, variables) => {
            const {key} = variables

            queryClient.removeQueries({
                queryKey: userPreferencesManagementKeys.preference(key),
            })

            queryClient
                .invalidateQueries({
                    queryKey: userPreferencesManagementKeys.user(),
                })
                .catch((error: unknown) => {
                    console.error(
                        `Error invalidating preferences: ${typeof error === 'string' ? error : String(error)}`,
                    )
                })
        },
    })
}

export function useQueryCurrentUser(
    options?: UseQueryOptions<User, Error>,
): UseQueryResult<User, Error> {
    return useQuery<User, Error>({
        retry: false,
        queryKey: userPreferencesManagementKeys.currentUser(),
        queryFn: () => fetchCurrentUser(),
        staleTime: 5 * 60 * 1000,
        ...options,
    })
}

export function useQuerySavedFilters<T, R = Record<string, T>>(
    key: string,
    select?: (data: Record<string, T>) => R,
): UseQueryResult<R, Error> {
    return useQuery<Record<string, T>, Error, R>({
        queryKey: userPreferencesManagementKeys.preference(key),
        queryFn: async () => {
            const result = await getRawUserPreference<Record<string, T>>(key)
            return result?.value ?? {}
        },
        select,
    })
}

export function useMutationSaveFilter() {
    const queryClient = useQueryClient()
    return useMutation<undefined, Error, {key: string; filterName: string; filter: unknown}>({
        mutationFn: async ({key, filterName, filter}) => {
            const result = await getRawUserPreference(key)
            const currentFilters = (result?.value ?? {}) as Record<string, unknown>

            const newFilters = {
                ...currentFilters,
                [filterName]: filter,
            }

            await updateRawUserPreference(key, newFilters)
        },
        onSuccess: (_, variables) => {
            void queryClient.invalidateQueries({
                queryKey: userPreferencesManagementKeys.preference(variables.key),
            })
        },
    })
}

export function useMutationDeleteFilter() {
    const queryClient = useQueryClient()
    return useMutation<undefined, Error, {key: string; filterName: string}>({
        mutationFn: async ({key, filterName}) => {
            const result = await getRawUserPreference(key)
            const currentFilters = (result?.value ?? {}) as Record<string, unknown>

            const newFilters: Record<string, unknown> = {}
            Object.keys(currentFilters).forEach((k) => {
                if (k !== filterName) {
                    newFilters[k] = currentFilters[k]
                }
            })

            await updateRawUserPreference(key, newFilters)
        },
        onSuccess: (_, variables) => {
            void queryClient.invalidateQueries({
                queryKey: userPreferencesManagementKeys.preference(variables.key),
            })
        },
    })
}
